import type {
  ImpactCategory,
  MatrixCellData,
  MatrixEntry,
  RiskScoringLevel,
  RiskScoringSettingsActions,
  RiskScoringSettingsState,
} from '@risksmart-app/atomic-ui';
import { getColorStyles } from '@risksmart-app/components/src/utils/colours';
import { useEffect, useRef } from 'react';
import { useGetLatestRiskAssessmentResultConfig } from 'src/hooks/queries';
import { create } from 'zustand';

import type { ScoringSettingsData } from './change-detection';
import { detectChanges } from './change-detection';
import {
  areImpactCategoriesComplete,
  areImpactLevelsComplete,
  areLikelihoodLevelsComplete,
  isMatrixComplete,
} from './validation';

// --- Internal store types ---

type State = {
  isMultiImpactEnabled: boolean;
  selectedMethodology: 'impact-likelihood' | 'multi-impact';
  likelihoodLevels: RiskScoringLevel[];
  impactLevels: RiskScoringLevel[];
  impactCategories: ImpactCategory[];
  matrix: MatrixEntry[];
  isMatrixInverted: boolean;
  initialData: ScoringSettingsData | null;
};

type Actions = {
  hydrate: (data: ScoringSettingsData) => void;
  reset: () => void;
  setMultiImpactEnabled: (enabled: boolean) => void;
  setSelectedMethodology: (
    methodology: 'impact-likelihood' | 'multi-impact'
  ) => void;
  addLikelihoodLevel: () => void;
  addImpactLevel: () => void;
  addImpactCategory: () => void;
  deleteLikelihoodLevel: (rating: number) => void;
  deleteImpactLevel: (rating: number) => void;
  deleteImpactCategory: (name: string) => void;
  matrixCellClick: (cell: MatrixCellData) => void;
  setMatrixInverted: (inverted: boolean) => void;
  getHasPendingChanges: () => boolean;
};

// --- Helpers ---

const getNextRating = (levels: { rating: number }[]) => {
  if (levels.length === 0) {
    return 1;
  }

  return Math.max(...levels.map((level) => level.rating)) + 1;
};

const initialState: State = {
  isMultiImpactEnabled: false,
  selectedMethodology: 'impact-likelihood',
  likelihoodLevels: [],
  impactLevels: [],
  impactCategories: [],
  matrix: [],
  isMatrixInverted: false,
  initialData: null,
};

// --- Internal store ---

export const useInternalStore = create<State & Actions>()((set, get) => ({
  ...initialState,

  hydrate: (data) => {
    set({
      likelihoodLevels: data.likelihoodLevels,
      impactLevels: data.impactLevels,
      impactCategories: data.impactCategories,
      matrix: data.matrix,
      initialData: { ...data },
    });
  },

  reset: () => {
    set(initialState);
  },

  setMultiImpactEnabled: (enabled) => {
    set({ isMultiImpactEnabled: enabled });
  },

  setSelectedMethodology: (methodology) => {
    set({ selectedMethodology: methodology });
  },

  addLikelihoodLevel: () => {
    set((state) => {
      const nextRating = getNextRating(state.likelihoodLevels);
      const fallbackColor =
        state.likelihoodLevels[state.likelihoodLevels.length - 1]?.color ??
        '#E5E7EB';

      return {
        likelihoodLevels: [
          ...state.likelihoodLevels,
          {
            rating: nextRating,
            title: `Level ${nextRating}`,
            description: '-',
            color: fallbackColor,
          },
        ],
      };
    });
  },

  addImpactLevel: () => {
    set((state) => {
      const nextRating = getNextRating(state.impactLevels);
      const fallbackColor =
        state.impactLevels[state.impactLevels.length - 1]?.color ?? '#E5E7EB';

      return {
        impactLevels: [
          ...state.impactLevels,
          {
            rating: nextRating,
            title: `Level ${nextRating}`,
            description: '-',
            color: fallbackColor,
          },
        ],
      };
    });
  },

  addImpactCategory: () => {
    set((state) => ({
      impactCategories: [
        ...state.impactCategories,
        {
          name: `New Category ${state.impactCategories.length + 1}`,
          color: '#474771',
        },
      ],
    }));
  },

  deleteLikelihoodLevel: (rating) => {
    set((state) => {
      const matrix = state.matrix
        .map((entry) => ({
          ...entry,
          likelihoodImpact: entry.likelihoodImpact.filter(
            (pair) => pair.likelihood !== rating
          ),
        }))
        .filter((entry) => entry.likelihoodImpact.length > 0);

      return {
        likelihoodLevels: state.likelihoodLevels.filter(
          (level) => level.rating !== rating
        ),
        matrix,
      };
    });
  },

  deleteImpactLevel: (rating) => {
    set((state) => {
      const matrix = state.matrix
        .map((entry) => ({
          ...entry,
          likelihoodImpact: entry.likelihoodImpact.filter(
            (pair) => pair.impact !== rating
          ),
        }))
        .filter((entry) => entry.likelihoodImpact.length > 0);

      return {
        impactLevels: state.impactLevels.filter(
          (level) => level.rating !== rating
        ),
        matrix,
      };
    });
  },

  deleteImpactCategory: (name) => {
    set((state) => ({
      impactCategories: state.impactCategories.filter(
        (category) => category.name !== name
      ),
    }));
  },

  matrixCellClick: (cell) => {
    console.debug('Matrix cell clicked', cell);
  },

  setMatrixInverted: (inverted) => {
    set({ isMatrixInverted: inverted });
  },

  getHasPendingChanges: () => {
    const state = get();

    if (!state.initialData) {
      return false;
    }

    return detectChanges(state.initialData, {
      likelihoodLevels: state.likelihoodLevels,
      impactLevels: state.impactLevels,
      impactCategories: state.impactCategories,
      matrix: state.matrix,
    }).hasChanges;
  },
}));

// --- Public wrapper hook ---

export interface RiskScoringSettingsStoreValue {
  state: RiskScoringSettingsState;
  actions: RiskScoringSettingsActions;
  isLoading: boolean;
}

export const useRiskScoringSettingsStore =
  (): RiskScoringSettingsStoreValue => {
    const { config, loading } = useGetLatestRiskAssessmentResultConfig();
    const store = useInternalStore();
    const hasHydratedRef = useRef(false);

    useEffect(() => {
      if (loading || hasHydratedRef.current) {
        return;
      }

      hasHydratedRef.current = true;

      const mapRating = (r: {
        value: number;
        title: string;
        description?: string | null;
        color: string;
      }) => ({
        rating: r.value,
        title: r.title,
        description: r.description ?? '',
        color: getColorStyles(r.color).backgroundColor,
      });

      store.hydrate({
        likelihoodLevels:
          config?.likelihood?.ratings
            ?.map(mapRating)
            .sort((a, b) => a.rating - b.rating) ?? [],
        impactLevels:
          config?.impact?.ratings
            ?.map(mapRating)
            .sort((a, b) => a.rating - b.rating) ?? [],
        impactCategories:
          config?.impact?.categories?.map((c) => ({
            name: c.name,
            color: getColorStyles(c.color).backgroundColor,
          })) ?? [],
        matrix:
          config?.matrix?.map((e) => ({
            title: e.title,
            value: e.value,
            color: getColorStyles(e.color).backgroundColor,
            likelihoodImpact: e.likelihoodImpact,
          })) ?? [],
      });
    }, [loading, config, store]);

    useEffect(() => {
      return () => {
        hasHydratedRef.current = false;
        useInternalStore.getState().reset();
      };
    }, []);

    const likelihoodComplete = areLikelihoodLevelsComplete(
      store.likelihoodLevels
    );
    const impactComplete = areImpactLevelsComplete(store.impactLevels);
    const categoriesComplete = areImpactCategoriesComplete(
      store.impactCategories
    );
    const matrixComplete = isMatrixComplete(
      store.matrix,
      store.likelihoodLevels.length,
      store.impactLevels.length
    );

    const state: RiskScoringSettingsState = {
      hasPendingChanges: store.getHasPendingChanges(),
      isMultiImpactEnabled: store.isMultiImpactEnabled,
      selectedMethodology: store.selectedMethodology,
      likelihoodLevels: store.likelihoodLevels,
      impactLevels: store.impactLevels,
      impactCategories: store.impactCategories,
      matrix: store.matrix,
      isMatrixInverted: store.isMatrixInverted,
      isLikelihoodLevelsComplete: likelihoodComplete,
      isImpactLevelsComplete: impactComplete,
      isImpactCategoriesComplete: categoriesComplete,
      isMatrixComplete: matrixComplete,
      isImpactLikelihoodComplete:
        likelihoodComplete && impactComplete && matrixComplete,
      isMultiImpactComplete: categoriesComplete,
    };

    const actions: RiskScoringSettingsActions = {
      onMultiImpactEnabledChange: store.setMultiImpactEnabled,
      onSelectedMethodologyChange: store.setSelectedMethodology,
      onAddLikelihoodLevel: store.addLikelihoodLevel,
      onAddImpactLevel: store.addImpactLevel,
      onAddImpactCategory: store.addImpactCategory,
      onDeleteLikelihoodLevel: store.deleteLikelihoodLevel,
      onDeleteImpactLevel: store.deleteImpactLevel,
      onDeleteImpactCategory: store.deleteImpactCategory,
      onMatrixCellClick: store.matrixCellClick,
      onInvertMatrixChange: store.setMatrixInverted,
    };

    return { state, actions, isLoading: loading };
  };
